import { QueryFactory } from '@lmt/shared-types';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import React, { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { HL7FilterDto } from '@lmt/shared-types';

import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { Loading } from '../../common/Loading';
import { Button, ButtonColor } from '../../common/ui-kit/buttons/Button';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import styles from './HL7FiltersPage.module.scss';
import AddIcon from '@lmt/unicorn-shared/app/icons/plus-blue.svg?react';

import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { HL7FiltersTable } from './list/HL7FiltersTable';

import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';

const defaultSortingFunction = (a: HL7FilterDto, b: HL7FilterDto) =>
  a.position === b.position
    ? (a.messageType ?? 0) - (b.messageType ?? 0)
    : a.position - b.position;
const sortFilters = (data: HL7FilterDto[]) => data.sort(defaultSortingFunction);

export const HL7FiltersPage: React.FC = React.memo(function HL7FiltersPage() {
  const globalForms = useGlobalForms();
  const i18n = useScopedTranslation('hl7.filters.list');
  const queryCache = useQueryClient();
  const dispatch = useUniDispatch();
  const { data: filters, isLoading: isLoading } =
    QueryFactory.HL7FilterQuery.useGetAllQuery({
      select: sortFilters,
    });

  const nonDefaultFilters = useMemo(() => {
    return filters?.filter((filter) => !filter.isDefault) ?? [];
  }, [filters]);
  const defaultFilters = useMemo(() => {
    const result = filters?.filter((filter) => filter.isDefault) ?? [];
    result.sort((a, b) =>
      // fallback filter (with messageType === null) should be displayed last
      a.messageType === null
        ? 1
        : b.messageType === null
          ? -1
          : defaultSortingFunction(a, b),
    );
    return result;
  }, [filters]);

  const onEditFilter = useCallback((filter: HL7FilterDto) => {
    globalForms.openHL7Filter(filter.id);
  }, []);
  const onNewFilter = useCallback(() => {
    globalForms.openHL7Filter(undefined);
  }, []);

  const onDeleteFilter = useCallback(
    async (filterId: number) => {
      try {
        await QueryFactory.HL7FilterClient.remove(filterId);
        await queryCache.invalidateQueries({
          queryKey: QueryFactory.HL7FilterQuery.getAllQueryKey(),
        });
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      }
    },
    [dispatch],
  );

  const modals = useModal();
  const onSortEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!nonDefaultFilters || !over || active.id === over.id) return;
      const oldIndex = nonDefaultFilters.findIndex(
        (x, i) => i + 1 == active.id,
      );
      const newIndex = nonDefaultFilters.findIndex((x, i) => i + 1 == over.id);

      const newData = arrayMove(nonDefaultFilters!, oldIndex, newIndex);
      newData.forEach((filter, i) => (filter.position = i + 1));
      queryCache.setQueryData(
        QueryFactory.HL7FilterQuery.getAllQueryKey(),
        defaultFilters.concat(newData),
      );

      try {
        await Promise.all(
          newData.map((x, i) => {
            return QueryFactory.HL7FilterClient.patch(x.id, {
              position: x.position,
            });
          }),
        );
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      }
    },
    [filters, nonDefaultFilters, defaultFilters],
  );

  const onEnabledChanged = useCallback(
    async (filterId: number, value: boolean) => {
      try {
        await QueryFactory.HL7FilterClient.patch(filterId, {
          enabled: value,
        });
        await queryCache.invalidateQueries({
          queryKey: QueryFactory.HL7FilterQuery.getAllQueryKey(),
        });
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      }
    },
    [dispatch],
  );

  return (
    <Loading loading={isLoading} title={i18n.t('loading')}>
      {filters ? (
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.title}>{i18n.t('custom_settings')}</div>
            <Button
              title={i18n.t('add_button')}
              color={ButtonColor.Primary}
              onClick={onNewFilter}
              iconComponent={<AddIcon />}
            />
          </div>
          <HL7FiltersTable
            data={nonDefaultFilters}
            editFilter={onEditFilter}
            removeFilter={onDeleteFilter}
            isNoFiltersFound={false}
            isDefault={false}
            onSortEnd={onSortEnd}
            onEnabledChanged={onEnabledChanged}
          />
          <div className={styles.header}>
            <div className={styles.title}>{i18n.t('default_settings')}</div>
          </div>
          <HL7FiltersTable
            data={defaultFilters}
            editFilter={onEditFilter}
            removeFilter={onDeleteFilter}
            isNoFiltersFound={false}
            isDefault={true}
            onSortEnd={onSortEnd}
            onEnabledChanged={onEnabledChanged}
          />
        </div>
      ) : null}
    </Loading>
  );
});
